/**
 * Routes the install flow to the right surface based on platform.
 *
 * Desktop / Android: branded `<InstallPrompt />` modal triggered after a value
 *   moment (we wait for `beforeinstallprompt` *and* a small session delay so
 *   we don't ambush first-time visitors).
 * iOS Safari: bottom-sheet walkthrough (`<IosInstallSheet />`) triggered after
 *   the same delay since Safari can't fire `beforeinstallprompt`.
 * iOS Chrome/other: slim banner directing the user back to Safari.
 *
 * Each surface remembers its own dismissal cooldown so suppressing one
 * doesn't suppress another.
 */

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useInstallPrompt, usePlatform, usePromptMemory } from '@/hooks/pwa'
import { InstallPrompt } from './install-prompt'
import { IosChromeBanner } from './ios-chrome-banner'
import { IosInstallSheet } from './ios-install-sheet'

const VALUE_MOMENT_DELAY_MS = 45_000

export function InstallExperience() {
  const platform = usePlatform()
  const { canInstall, justInstalled } = useInstallPrompt()
  const [installModalOpen, setInstallModalOpen] = useState(false)
  const [iosSheetOpen, setIosSheetOpen] = useState(false)
  const [iosChromeBannerOpen, setIosChromeBannerOpen] = useState(false)

  const installMemory = usePromptMemory('install', 30)
  const iosMemory = usePromptMemory('ios-walkthrough', 14)
  const iosChromeMemory = usePromptMemory('ios-chrome', 30)

  // Confirm to the user that the install completed; this fires once per session.
  useEffect(() => {
    if (!justInstalled) return
    toast.success('Taqat installed', {
      description: launchHint(platform.platform),
    })
  }, [justInstalled, platform.platform])

  // Skip every prompt path entirely once running as a standalone PWA.
  useEffect(() => {
    if (platform.isStandalone) return

    const timer = window.setTimeout(() => {
      if (canInstall && installMemory.isEligible && !platform.isIOS) {
        setInstallModalOpen(true)
        return
      }
      if (platform.platform === 'ios-safari' && iosMemory.isEligible) {
        setIosSheetOpen(true)
        return
      }
      if (platform.platform === 'ios-other' && iosChromeMemory.isEligible) {
        setIosChromeBannerOpen(true)
      }
    }, VALUE_MOMENT_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [
    canInstall,
    installMemory.isEligible,
    iosMemory.isEligible,
    iosChromeMemory.isEligible,
    platform.isIOS,
    platform.isStandalone,
    platform.platform,
  ])

  if (platform.isStandalone) return null

  return (
    <>
      <InstallPrompt open={installModalOpen} onOpenChange={setInstallModalOpen} />
      <IosInstallSheet open={iosSheetOpen} onOpenChange={setIosSheetOpen} />
      <IosChromeBanner show={iosChromeBannerOpen} onDismiss={() => setIosChromeBannerOpen(false)} />
    </>
  )
}

function launchHint(platform: string): string {
  if (platform === 'android-installable') return 'Find it on your home screen.'
  if (platform === 'desktop-installable') return 'Find it in your dock or Start menu.'
  return 'Open Taqat from your installed apps.'
}
